import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Service } from "../../../models/ServiceModel";
import useServicesService from "../../../services/useServicesService";
import showToast from "../../../utils/toast";
import { toYmdLocal } from "../../../utils/formatDate";
import {
  getMinServiceDateYmd,
  isServiceDateTimeInPast,
} from "../../../utils/serviceScheduleValidation";
import type { ServiceResponse } from "../../../hooks/useFetch";
import { isEnaexCompany } from "../../../config/adicionalCompany";
import ServiceCloneFormFields, {
  ServiceClonePayload,
  useServiceCloneLocations,
} from "./ServiceCloneFormFields";
import "./styles/ServiceDuplicateModal.css";

interface ServiceReturnModalProps {
  isOpen: boolean;
  service: Service | null;
  onClose: () => void;
  onSuccess?: (newServiceId: number) => void;
}

function normalizeServiceTime(time?: string | null): string {
  if (!time) return "";
  const t = String(time).trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!match) return t;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function resolveReturnServiceDate(
  raw: string | null | undefined,
  minDateYmd: string
): string {
  let ymd = String(raw ?? "").trim().split("T")[0];
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(ymd);
  if (dmy) {
    ymd = `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  }
  const today = toYmdLocal();
  const fallback = today >= minDateYmd ? today : minDateYmd;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd) || ymd < minDateYmd) {
    return fallback;
  }
  return ymd;
}

function locationId(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "object" && value !== null && "id" in value) {
    const id = (value as { id?: number | string }).id;
    return id != null ? String(id) : "";
  }
  return String(value);
}

const ServiceReturnModal: React.FC<ServiceReturnModalProps> = ({
  isOpen,
  service,
  onClose,
  onSuccess,
}) => {
  const { retorno, fetching } = useServicesService();
  const { locationOptions, loadingLocations } = useServiceCloneLocations(isOpen);
  const minDateYmd = getMinServiceDateYmd();
  const [serviceDate, setServiceDate] = useState("");
  const [serviceTime, setServiceTime] = useState("");
  const [originId, setOriginId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [enaexCode, setEnaexCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const isEnaex = isEnaexCompany(service?.company_name);
  const sourceEnaexCode = (service?.enaex_code ?? "").trim();

  useEffect(() => {
    if (!isOpen || !service) return;
    setServiceDate(resolveReturnServiceDate(service.service_date, minDateYmd));
    setServiceTime(normalizeServiceTime(service.service_time));
    // Retorno por defecto: origen/destino invertidos (igual que el backend).
    setOriginId(locationId(service.destination));
    setDestinationId(locationId(service.origin));
    setEnaexCode("");
    setFieldError(null);
  }, [isOpen, service, minDateYmd]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isOpen) return;
      if (document.querySelector(".a11y-date__popover, .a11y-time__popover")) return;
      onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !service?.id) return null;

  const isPastDate = Boolean(serviceDate) && serviceDate < minDateYmd;
  const dateTimeInPast = isServiceDateTimeInPast(serviceDate, serviceTime);
  const enaexCodeTrim = enaexCode.trim();
  const enaexCodeSameAsSource =
    isEnaex &&
    Boolean(sourceEnaexCode) &&
    enaexCodeTrim.toLowerCase() === sourceEnaexCode.toLowerCase();
  const isValid =
    Boolean(serviceDate) &&
    Boolean(serviceTime) &&
    !isPastDate &&
    !dateTimeInPast &&
    Boolean(originId) &&
    Boolean(destinationId) &&
    (!isEnaex || (Boolean(enaexCodeTrim) && !enaexCodeSameAsSource));

  const defaultOriginLabel = service.destination_name ?? null;
  const defaultDestinationLabel = service.origin_name ?? null;

  const swapRoute = () => {
    setOriginId(destinationId);
    setDestinationId(originId);
    setFieldError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !service.id) return;

    const payload: ServiceClonePayload & { enaex_code?: string } = {
      service_date: serviceDate,
      service_time: serviceTime,
      change_origin: true,
      origin_id: Number(originId),
      change_destination: true,
      destination_id: Number(destinationId),
    };
    if (isEnaex) {
      payload.enaex_code = enaexCodeTrim;
    }

    retorno(service.id, payload, {
      onSuccess: (r: ServiceResponse) => {
        const newId =
          r?.data?.id ??
          (typeof r?.data === "object" && r?.data !== null && "id" in r.data
            ? Number((r.data as { id: number }).id)
            : undefined);
        showToast(r?.message || "Retorno generado correctamente", "success");
        onClose();
        if (newId) onSuccess?.(Number(newId));
      },
      onError: (r: ServiceResponse) => {
        const msg =
          r?.message ||
          (typeof r?.data === "object" && r?.data
            ? Object.values(r.data as Record<string, unknown>)
                .flat()
                .join(" ")
            : "No se pudo generar el retorno");
        setFieldError(String(msg));
        showToast(String(msg), "error");
      },
    });
  };

  const content = (
    <div
      className="service-duplicate-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="service-duplicate-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-return-title"
      >
        <div className="service-duplicate-header">
          <div>
            <h3 id="service-return-title" className="service-duplicate-title">
              Retorno del servicio #{service.id}
            </h3>
            <p className="service-duplicate-subtitle">
              Indica fecha, hora, origen y destino del retorno. Por defecto se invierte la
              ruta. El conductor no se copia.
            </p>
          </div>
          <button
            type="button"
            className="service-duplicate-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>

        <form className="service-duplicate-form" onSubmit={handleSubmit}>
          <ServiceCloneFormFields
            dateFieldId="return_service_date"
            timeFieldId="return_service_time"
            serviceDate={serviceDate}
            serviceTime={serviceTime}
            minDateYmd={minDateYmd}
            onDateChange={(e) => {
              setServiceDate(e.target.value);
              setFieldError(null);
            }}
            onTimeChange={(e) => {
              setServiceTime(e.target.value);
              setFieldError(null);
            }}
            fullRoute
            originId={originId}
            onOriginChange={(value) => {
              setOriginId(value);
              setFieldError(null);
            }}
            destinationId={destinationId}
            onDestinationChange={(value) => {
              setDestinationId(value);
              setFieldError(null);
            }}
            onSwapRoute={swapRoute}
            locationOptions={locationOptions}
            loadingLocations={loadingLocations}
            currentOriginLabel={defaultOriginLabel}
            currentDestinationLabel={defaultDestinationLabel}
            routeHintLabel="Retorno por defecto"
          />

          {isEnaex && (
            <div className="service-duplicate-enaex">
              <label className="service-duplicate-enaex-label" htmlFor="return_enaex_code">
                Código ENAEX (nuevo)
              </label>
              <input
                id="return_enaex_code"
                type="text"
                className="service-duplicate-enaex-input"
                value={enaexCode}
                onChange={(e) => {
                  setEnaexCode(e.target.value);
                  setFieldError(null);
                }}
                placeholder="Ingrese el code del retorno"
                maxLength={128}
                autoComplete="off"
                required
              />
              {sourceEnaexCode ? (
                <p className="service-duplicate-hint">
                  Código del servicio original: <strong>{sourceEnaexCode}</strong>
                </p>
              ) : null}
              {enaexCodeSameAsSource && (
                <p className="service-duplicate-error" role="alert">
                  Debe ingresar un código ENAEX distinto al del servicio original.
                </p>
              )}
            </div>
          )}

          {dateTimeInPast && (
            <p className="service-duplicate-error" role="alert">
              La fecha y hora no puede ser anterior a la hora actual.
            </p>
          )}

          {fieldError && (
            <p className="service-duplicate-error service-duplicate-error--api" role="alert">
              {fieldError}
            </p>
          )}

          <div className="service-duplicate-actions">
            <button
              type="button"
              className="service-duplicate-btn service-duplicate-btn-cancel"
              onClick={onClose}
              disabled={fetching.retorno}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="service-duplicate-btn service-duplicate-btn-submit"
              disabled={!isValid || fetching.retorno}
            >
              {fetching.retorno ? "Generando..." : "Generar retorno"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ServiceReturnModal;
